import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { RefreshBalanceBtn } from "@components/buttons";
import LoginHistory from "@components-shared/mobile/LoginHistory";
import UserBalanceDisplay from "@components-shared/mobile/UserBalanceDisplay";
import { useRootAppStore } from "@hooks";
import { ChatEntryPoint } from "@data-types/chat";
import { observer } from "mobx-react";
import { isGamesTheme } from "@utils";
import { isCapacitorPlatform } from "@utils/specific/capacitor";
import { NotificationInfo } from "@features/notifications/notification-info/NotificationInfo";
import { useNotification } from "@utils/notifications";
import { ChatViewStore } from "@state/stores/chat/ChatViewStore";
import GamesMenuOption from "./GamesMenuOption";
import { THEME_SETTINGS } from "@theme-settings";
import { useAppContext } from "@state/hooks/shared";
import { getLoginReturnUrl } from "@utils/auth";

type UserMenuOptionsProps = {
	logout: Function;
	setIsNavMenuOpen: Function;
};

export function UserMenuOptions(props: UserMenuOptionsProps) {
	const { logout, setIsNavMenuOpen } = props;

	const rootStore = useRootAppStore();

	const { culture } = useAppContext();
	const { t } = useTranslation();

	const requiredBtnClasses = classNames(
		"btn btn--med btn--block btn--icon u-mar--top--xsml",
		{
			"btn--danger":
				rootStore.userAuthStore.isUserCritical ||
				(rootStore.userAuthStore.isUserCritical &&
					rootStore.userAuthStore.isUserWarning),
			"btn--warning":
				rootStore.userAuthStore.isUserWarning &&
				!rootStore.userAuthStore.isUserCritical,
			"btn--primary--inverted":
				!rootStore.userAuthStore.isUserWarning &&
				!rootStore.userAuthStore.isUserCritical,
		}
	);

	const requiredIconClasses = classNames("u-icon u-icon--sml", {
		"u-icon--alert-triangle u-color--core--snow":
			rootStore.userAuthStore.isUserCritical ||
			(rootStore.userAuthStore.isUserCritical &&
				rootStore.userAuthStore.isUserWarning),
		"u-icon--alert-circle u-color--core--nero":
			rootStore.userAuthStore.isUserWarning &&
			!rootStore.userAuthStore.isUserCritical,
		"u-icon--info-circle u-color--sent--warning":
			!rootStore.userAuthStore.isUserCritical &&
			!rootStore.userAuthStore.isUserWarning,
	});

	if (rootStore.userAuthStore.user == null) {
		return (
			<>
				<div className="sidebar__section">
					<div className="sidebar__title">
						<div className="title--sidebar">
							{t<string>("HEADER.USER_ACCOUNT")}
						</div>
					</div>
					<nav className="nav--secondary">
						<ul className="nav--secondary__menu">
							<li
								className="nav--secondary__item"
								onClick={() => setIsNavMenuOpen(false)}
							>
								<Link
									to={getLoginReturnUrl()}
									className="nav--secondary__link"
								>
									<div className="nav--secondary__inner">
										<div>{t<string>("HEADER.LOGIN")}</div>
										<small className="nav--secondary__description">
											{t<string>(
												"HEADER.LOG_IN_TO_ACCOUNT"
											)}
										</small>
									</div>
								</Link>
							</li>
							{WEBPACK_IS_REGISTRATION_ENABLED === true && (
								<li
									className="nav--secondary__item"
									onClick={() => setIsNavMenuOpen(false)}
								>
									<RegisterNavigation />
								</li>
							)}
							<QrCodeScannerLink
								setIsNavMenuOpen={(value) =>
									setIsNavMenuOpen(value)
								}
							/>
							<SupportChatLink
								chatViewStore={rootStore.chatViewStore}
								setIsNavMenuOpen={(value) =>
									setIsNavMenuOpen(value)
								}
							/>
						</ul>
					</nav>
				</div>

				<GamesMenuOption />
			</>
		);
	}

	return (
		<>
			<div className="sidebar__account">
				<div className="sidebar__title">
					<Link
						to={`/${culture}/account-settings`}
						className="title--sidebar sidebar__account__link"
						onClick={() => setIsNavMenuOpen(false)}
					>
						<div className="sidebar__account__info">
							<span className="sidebar__account__user">
								{rootStore.userAuthStore.user?.username}
							</span>
						</div>
					</Link>
					<RefreshBalanceBtn
						atClassName="asideRefreshBalanceBtn"
						className="btn--square btn--square--sml btn--primary--inverted sidebar__refresh"
						iconOnly={true}
						iconClassName="u-icon--sml u-icon--refresh"
					/>
				</div>

				<div className="sidebar__balance">
					<ul className="wallet">
						<UserBalanceDisplay isInMenu={true} />
					</ul>
				</div>

				<div className="sidebar__history">
					<LoginHistory />

					{rootStore.requiredUserActionStore.userActionStatus && (
						<button
							type="button"
							className={requiredBtnClasses}
							onClick={() => {
								rootStore.requiredUserActionStore.showPopup();
								setIsNavMenuOpen(false);
							}}
						>
							<i className={requiredIconClasses}></i>
							<span className="btn__label">
								{t("WELCOME_POPUP.REQUIRED_ACTIONS_TITLE")}
							</span>
						</button>
					)}
				</div>
			</div>

			<div className="sidebar__section">
				<div className="sidebar__title">
					<div className="title--sidebar">
						{t<string>("HEADER.MY_ACCOUNT")}
					</div>
				</div>

				<UserMenuNavigation
					chatViewStore={rootStore.chatViewStore}
					logout={logout}
					setIsNavMenuOpen={setIsNavMenuOpen}
				/>
			</div>

			<GamesMenuOption />
		</>
	);
}

type UserMenuNavigationProps = {
	chatViewStore: ChatViewStore;
	logout: Function;
	setIsNavMenuOpen: Function;
};

const UserMenuNavigation = observer((props: UserMenuNavigationProps) => {
	const { t } = useTranslation("account-settings-navigation");
	const { culture } = useAppContext();
	const { setIsNavMenuOpen } = props;
	const rootStore = useRootAppStore();

	return (
		<>
			<nav className="nav--secondary">
				<ul className="nav--secondary__menu">
					{!isGamesTheme() && (
						<li
							className="nav--secondary__item"
							onClick={() => setIsNavMenuOpen(false)}
						>
							<Link
								to={`/${culture}/my-bets`}
								className="nav--secondary__link"
							>
								<span className="nav--secondary__inner">
									{t<string>("HEADER.MY_BETS")}
								</span>
								<i className="nav--secondary__arrow u-icon u-icon--tny u-icon--caret--right" />
							</Link>
						</li>
					)}
					<li
						className="nav--secondary__item"
						onClick={() => setIsNavMenuOpen(false)}
					>
						<Link
							to={`/${culture}/account-statement`}
							className="nav--secondary__link"
						>
							<span className="nav--secondary__inner">
								{t<string>("HEADER.ACCOUNT_STATEMENT")}
							</span>
							<i className="nav--secondary__arrow u-icon u-icon--tny u-icon--caret--right" />
						</Link>
					</li>
					{(WEBPACK_IS_CASINO_ENABLED ||
						WEBPACK_IS_CASINO_LIVE_ENABLED ||
						isGamesTheme()) && (
						<li
							className="nav--secondary__item"
							onClick={() => setIsNavMenuOpen(false)}
						>
							<Link
								to={`/${culture}/gaming-history`}
								className="nav--secondary__link"
							>
								<span className="nav--secondary__inner">
									{t<string>("GH_HEAD")}
								</span>
								<i className="nav--secondary__arrow u-icon u-icon--tny u-icon--caret--right" />
							</Link>
						</li>
					)}

					{rootStore.userAuthStore.isInternetUser ? (
						<>
							<li
								className="nav--secondary__item "
								onClick={() => setIsNavMenuOpen(false)}
							>
								<Link
									to={`/${culture}/deposit`}
									className="nav--secondary__link"
								>
									<span className="nav--secondary__inner">
										{t<string>("DEPOSIT.TITLE")}
									</span>
									<i className="nav--secondary__arrow u-icon u-icon--tny u-icon--caret--right" />
								</Link>
							</li>
							<li
								className="nav--secondary__item "
								onClick={() => setIsNavMenuOpen(false)}
							>
								<Link
									to={`/${culture}/withdrawal`}
									className="nav--secondary__link"
								>
									<span className="nav--secondary__inner">
										{t<string>("WITHDRAWAL.TITLE")}
									</span>
									<i className="nav--secondary__arrow u-icon u-icon--tny u-icon--caret--right" />
								</Link>
							</li>
						</>
					) : null}

					<li
						className="nav--secondary__item "
						onClick={() => setIsNavMenuOpen(false)}
					>
						<Link
							to={`/${culture}/account-settings`}
							className="nav--secondary__link"
						>
							<span className="nav--secondary__inner">
								{t<string>("HEADER.ACCOUNT_SETTINGS")}
							</span>
							<i className="nav--secondary__arrow u-icon u-icon--tny u-icon--caret--right" />
						</Link>
					</li>

					<ChatNavigation setIsNavMenuOpen={setIsNavMenuOpen} />

					<NotificationSettingsNavigation
						setIsNavMenuOpen={setIsNavMenuOpen}
					/>

					<QrCodeScannerLink
						setIsNavMenuOpen={(value) => setIsNavMenuOpen(value)}
					/>
					<SupportChatLink
						chatViewStore={props.chatViewStore}
						setIsNavMenuOpen={(value) => setIsNavMenuOpen(value)}
					/>

					<li className="nav--secondary__item nav--secondary__item--full">
						<button
							className="at-asideLogoutBtn btn btn--sml btn--block btn--primary--inverted btn--icon"
							onClick={async () => {
								await rootStore.logoutPopupStore.displayLogoutOption();

								setIsNavMenuOpen(false);
							}}
						>
							<i className="u-icon u-icon--sml u-icon--logout" />
							<span className="btn__label">
								{t<string>("HEADER.LOGOUT")}
							</span>
						</button>
					</li>
				</ul>
			</nav>
		</>
	);
});

function NotificationSettingsNavigation(props: { setIsNavMenuOpen: Function }) {
	const { culture } = useAppContext();
	const { t } = useTranslation("notification-settings");

	if (!isCapacitorPlatform()) {
		return null;
	}

	if (isGamesTheme()) {
		return null;
	}

	return (
		<>
			<li
				className="nav--secondary__item"
				onClick={() => props.setIsNavMenuOpen(false)}
			>
				<Link
					to={`/${culture}/account-settings/notification-settings`}
					className="nav--secondary__link"
				>
					<span className="nav--secondary__inner">{t("TITLE")}</span>
					<i className="nav--secondary__arrow u-icon u-icon--tny u-icon--caret--right" />
				</Link>
			</li>
			<NotificationInfo isSidebar />
		</>
	);
}

const ChatNavigation = observer(function ChatNavigation(props: {
	setIsNavMenuOpen: Function;
}) {
	const { t } = useTranslation();
	const { chatViewStore } = useRootAppStore();

	const handleChatOpen = () => {
		if (!chatViewStore.isDisposing) {
			props.setIsNavMenuOpen(false);
			chatViewStore.onInitialize(ChatEntryPoint.USER_MENU, null);
		}
	};

	if (!WEBPACK_IS_CHAT_AVAILABLE) {
		return null;
	}

	return (
		<li className="nav--secondary__item" onClick={handleChatOpen}>
			<div className="nav--secondary__link">
				<span className="nav--secondary__inner u-padd--right--xtny">
					{t("CHAT.TITLE")}
				</span>
				{chatViewStore.isDisposing ? (
					<span className="btn--load btn--load--primary u-mar--auto"></span>
				) : (
					<i className="nav--secondary__arrow u-icon u-icon--tny u-icon--caret--right" />
				)}
			</div>
		</li>
	);
});

const QrCodeScannerLink = function QrCodeScannerLink({
	setIsNavMenuOpen,
}: {
	setIsNavMenuOpen: (value: boolean) => void;
}) {
	const { culture } = useAppContext();
	const { t } = useTranslation();
	if (!THEME_SETTINGS.showBetQRScanner) {
		return null;
	}
	return (
		<li
			className="nav--secondary__item nav--secondary__qr"
			onClick={() => setIsNavMenuOpen(false)}
		>
			<Link
				to={`/${culture}/check-bet-slip`}
				className="nav--secondary__link"
			>
				<div className="nav--secondary__inner">
					<div>{t<string>("QR_SCANNER.SIDEMENU.TITLE")}</div>
					<small className="nav--secondary__description">
						{t<string>("QR_SCANNER.SIDEMENU.DESCRIPTION")}
					</small>
				</div>
				<i className="u-icon u-icon--lrg u-icon--qr" />
			</Link>
		</li>
	);
};

const SupportChatLink = observer(function SupportChatLink({
	chatViewStore,
	setIsNavMenuOpen,
}: {
	chatViewStore: ChatViewStore;
	setIsNavMenuOpen: (value: boolean) => void;
}) {
	const { showInfo } = useNotification();
	const { unreadSupportMessages } = chatViewStore;

	const { culture } = useAppContext();
	const { t } = useTranslation();
	const userAuthStore = useRootAppStore().userAuthStore;

	if (!WEBPACK_IS_SUPPORT_CHAT_AVAILABLE) {
		return null;
	}

	return (
		<li
			className="nav--secondary__item"
			onClick={() => setIsNavMenuOpen(false)}
		>
			<Link
				onClick={(e) => {
					if (!userAuthStore.isLoggedIn) {
						e.preventDefault();
						showInfo(t("CHAT.ERR_HANDLING.PLEASE_LOGIN"));
					}
				}}
				to={`/${culture}/support-chat`}
				className="nav--secondary__link"
			>
				<div className="nav--secondary__inner">
					<div className="nav--secondary__title">
						<div className="nav--secondary__title__icon">
							<i className="u-icon u-icon--sml u-icon--support--alt u-pos--rel">
								{unreadSupportMessages && (
									<div className="tag tag--dot" />
								)}
							</i>
						</div>

						<div className="nav--secondary__title__clip">
							{t("CHAT.SUPPORT.TITLE")}
						</div>
					</div>
					<small className="nav--secondary__description">
						{t("CHAT.SUPPORT.SUBTITLE")}{" "}
					</small>
				</div>
				<i className="nav--secondary__caret u-icon u-icon--tny u-icon--caret--right u-mar--left--xtny" />
			</Link>
		</li>
	);
});

const RegisterNavigation = () => {
	const { culture } = useAppContext();
	const { t } = useTranslation();

	return (
		<Link to={`/${culture}/auth/register`} className="nav--secondary__link">
			<div className="nav--secondary__inner">
				<div>{t<string>("COMMON.REGISTRATION_TITLE")}</div>
				<small className="nav--secondary__description">
					{t<string>("COMMON.REGISTRATION_OPENING")}
				</small>
			</div>
		</Link>
	);
};
